<template>
  <div class="push-channel-select-wrap" :style="{ width }">
    <el-select
      :value="value"
      class="push-channel-select"
      :popper-class="popperClass"
      multiple
      :loading="pushChannelLoading"
      :disabled="isDisabled"
      :placeholder="placeholder"
      :no-data-text="noAvailableText"
      @input="onSelectInput"
      @visible-change="onVisibleChange"
    >
      <!-- 网页端未配置项被隐藏；无可用渠道时在下拉里给一条提示（非 option，不会被隐藏规则命中） -->
      <li
        v-if="noAvailableChannel && !isClient && optionList.length"
        class="push-channel-dropdown-hint"
      >{{ noAvailableText }}</li>
      <el-option
        v-for="opt in optionList"
        :key="opt.type"
        :value="opt.type"
        :label="opt.label"
        :disabled="!opt.available"
      >
        <span class="push-channel-option" @click="onOptionClick($event, opt)">
          <span class="push-channel-label">{{ opt.label }}</span>
          <span
            v-if="opt.available"
            class="push-channel-state"
            title="该渠道已配置，可直接选择"
          >
            <i class="fa fa-check-circle"></i>
            <span>已配置</span>
          </span>
          <span
            v-else
            class="push-channel-add"
            title="跳转到客户端能力中心配置"
            @click.stop.prevent="onConfigureChannel(opt)"
          >+配置渠道</span>
        </span>
      </el-option>
    </el-select>
    <p v-if="invalidTypes.length" class="push-channel-warning">
      <i class="el-icon-warning-outline"></i>
      <span>「{{ invalidText }}」渠道当前不可用（已失效或未配置），请重新配置后重新选择</span>
    </p>
  </div>
</template>

<script>
import { getPushChannelOptionsApi } from '@/api/agentAuth'
import { isCosmoSphere } from '@/utils/env'
import { getInvalidPushChannels, getPushChannelText } from '@/utils/pushChannel'

/**
 * 推送渠道多选下拉（定时任务各入口通用）。
 *
 * 职责：
 *  - 拉取渠道选项（进入 / 切换数字人 / 展开下拉时）；
 *  - 未配置渠道不可选中（客户端显示「+配置渠道」入口，网页端在下拉里隐藏）；
 *  - 已保存但当前不可用的渠道给出提示。
 * 表单值由父组件通过 v-model 持有；本组件只负责选项与展示。
 */
export default {
  name: 'PushChannelSelect',
  props: {
    /** 已选渠道 type 列表（v-model） */
    value: {
      type: Array,
      default: () => []
    },
    /** 数字人 / 智能体 id，用于拉取渠道选项 */
    agentId: {
      type: [String, Number],
      default: ''
    },
    /** 通知客户端打开连接器时上报的来源标识 */
    source: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '请选择推送渠道（不选则不推送）'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** 组件宽度，默认铺满；如 '400px'（与同表单其它控件对齐） */
    width: {
      type: String,
      default: '100%'
    }
  },
  data() {
    return {
      /** 接口返回的渠道原始列表 */
      pushChannelOptions: [],
      pushChannelLoading: false,
      /** 是否成功拉取过一次（用于失效对账，避免拉取失败误报） */
      pushChannelLoaded: false
    }
  },
  computed: {
    optionList() {
      return Array.isArray(this.pushChannelOptions) ? this.pushChannelOptions : []
    },
    isClient() {
      return isCosmoSphere()
    },
    isDisabled() {
      return this.disabled || !this.agentId
    },
    /** 下拉 popper 类名：网页端隐藏未配置项 */
    popperClass() {
      return isCosmoSphere()
        ? 'push-channel-dropdown'
        : 'push-channel-dropdown push-channel-dropdown--web'
    },
    /** 已保存但当前不可用的渠道 */
    invalidTypes() {
      return getInvalidPushChannels(this.pushChannelOptions, this.value, this.pushChannelLoaded)
    },
    invalidText() {
      return getPushChannelText(this.pushChannelOptions, this.invalidTypes)
    },
    /** 成功拉取过、且没有任何可用渠道（未成功拉取时不判定，避免接口失败误报） */
    noAvailableChannel() {
      if (!this.pushChannelLoaded || !this.agentId) return false
      return !this.optionList.some((o) => o && o.available)
    },
    /** 无可用渠道时的提示文案（分端） */
    noAvailableText() {
      return this.isClient
        ? '暂无可用的推送渠道，请先点击「+配置渠道」完成配置'
        : '暂无可用的推送渠道，请联系管理员完成渠道配置'
    }
  },
  watch: {
    agentId(newVal, oldVal) {
      if (!newVal) {
        this.pushChannelOptions = []
        this.pushChannelLoaded = false
        return
      }
      if (newVal === oldVal) return
      this.fetchOptions()
    }
  },
  created() {
    this.fetchOptions()
  },
  methods: {
    onSelectInput(val) {
      this.$emit('input', val)
    },
    onVisibleChange(visible) {
      if (!visible) return
      this.fetchOptions()
    },
    async fetchOptions() {
      const agentId = this.agentId
      if (!agentId) {
        this.pushChannelOptions = []
        this.pushChannelLoaded = false
        return
      }
      this.pushChannelLoading = true
      try {
        const res = await getPushChannelOptionsApi(agentId)
        const list = (res && res.data) || res || []
        this.pushChannelOptions = Array.isArray(list) ? list : []
        this.pushChannelLoaded = true
      } catch (e) {
        console.error('[PushChannelSelect] 获取推送渠道选项失败:', e)
        this.pushChannelOptions = []
        this.pushChannelLoaded = false
      } finally {
        this.pushChannelLoading = false
      }
    },
    /**
     * 点击「+配置渠道」：仅客户端走 postMessage 通知宿主跳转到能力中心-连接器。
     */
    onConfigureChannel(opt) {
      if (!opt) return
      if (!isCosmoSphere()) return
      try {
        window.parent.postMessage(
          {
            type: 'cosmo:open-connector',
            channelType: opt.type,
            channelLabel: opt.label,
            source: this.source
          },
          window.location.origin
        )
      } catch (e) {
        console.error('[PushChannelSelect] 通知客户端打开连接器失败:', e)
      }
    },
    /**
     * 点击渠道行：未配置渠道不可选中，给出提示；已配置渠道不拦截。
     */
    onOptionClick(e, opt) {
      if (!opt || opt.available) return
      e.stopPropagation()
      this.$message.warning(`「${opt.label}」渠道未配置，请先点击「+配置渠道」完成配置后再选择`)
    }
  }
}
</script>

<style lang="scss" scoped>
.push-channel-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.push-channel-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #334155;
  font-size: 12px;
}
/* 已配置渠道：右侧绿色对号 + 「已配置」文案 */
.push-channel-state {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  color: #10b981;
  font-size: 11px;

  .fa { font-size: 12px; }
}
/* 未配置渠道：右侧「+配置渠道」入口 */
.push-channel-add {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eef2ff;
  color: #4a5ff3;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover { background: #dde6ff; }
}
/* 失效渠道行内提示 */
.push-channel-warning {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: #e6a23c;

  i {
    flex-shrink: 0;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>

<style lang="scss">
/* 下拉 popper 与 el-select 内部结构由 Element 渲染，scoped 无法命中，故用非 scoped 样式 */
.push-channel-select {
  width: 100%;

  .el-select__tags {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    padding: 2px 30px 2px 10px;
    align-content: center;
  }

  .el-tag {
    height: 24px;
    line-height: 22px;
    margin: 3px 4px 3px 0;
    background: #eef2ff;
    border-color: #dbe2ff;
    color: #4a5ff3;

    .el-tag__close {
      color: #8b98f8;

      &:hover {
        background: #4a5ff3;
        color: #fff;
      }
    }
  }
}

.push-channel-dropdown.el-select-dropdown.is-multiple .el-select-dropdown__item {
  padding-right: 20px;
}

.push-channel-dropdown.el-select-dropdown.is-multiple .el-select-dropdown__item.selected::after {
  display: none;
}

.push-channel-dropdown.el-select-dropdown.is-multiple .el-select-dropdown__item.is-disabled .push-channel-label {
  color: #c0c4cc;
}

/* 网页端：未配置渠道不在下拉里展示（仍渲染，保证已保存渠道能解析出中文名） */
.push-channel-dropdown--web.el-select-dropdown.is-multiple .el-select-dropdown__item.is-disabled {
  display: none;
}

/* 下拉内的「无可用渠道」提示行（非 el-option，不受上面的隐藏规则影响） */
.push-channel-dropdown .push-channel-dropdown-hint {
  padding: 8px 20px;
  font-size: 12px;
  line-height: 1.5;
  color: #94a3b8;
  white-space: normal;
}
</style>
